import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { UserPlus, Trash2, Users, ShieldCheck, Loader2 } from 'lucide-react';

export default function BetaTesterPanel() {
  const [rows, setRows] = useState({ code_allowlist: [], db_extras: [], code_allowlist_size: 0, db_extras_size: 0, total_size: 0 });
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [note, setNote] = useState('');
  const [sendInvite, setSendInvite] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get('/admin/beta-testers');
      setRows(r.data || { code_allowlist: [], db_extras: [] });
    } catch (e) {
      toast.error('Could not load beta testers');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function onAdd(e) {
    e.preventDefault();
    const trimEmail = (email || '').trim().toLowerCase();
    const trimName = (firstName || '').trim();
    if (!trimEmail || !trimName) {
      toast.error('Email and first name are both required');
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post('/admin/beta-testers/add', {
        email: trimEmail,
        first_name: trimName,
        note: note.trim() || null,
        send_invite: sendInvite,
      });
      const invite = r.data?.invite;
      if (sendInvite) {
        if (invite?.sent) {
          toast.success(`${trimName} added — Sept 11 invite delivered ✨`);
        } else {
          toast.warning(`${trimName} added — invite failed (${invite?.error || 'unknown'})`);
        }
      } else {
        toast.success(`${trimName} added to the allowlist`);
      }
      setEmail(''); setFirstName(''); setNote('');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not add tester');
    } finally {
      setSubmitting(false);
    }
  }

  async function onRemove(row) {
    const label = `${row.first_name || row.email}`;
    if (!window.confirm(`Remove ${label} from the beta allowlist? This does not delete their account or emails already sent.`)) return;
    try {
      await api.delete(`/admin/beta-testers/${encodeURIComponent(row.email)}`);
      toast.success(`${label} removed`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Remove failed');
    }
  }

  return (
    <div className="space-y-8" data-testid="beta-tester-panel">
      {/* HEADER */}
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <p className="text-gold font-sans text-xs tracking-[0.3em] uppercase mb-2">Beta Roster</p>
          <h2 className="font-serif text-3xl text-dark">Beta Testers</h2>
          <p className="font-sans text-sm text-text-muted mt-1 max-w-xl">
            Add a new tester and (optionally) fire the Sept 11 invite in one step. Runtime-added testers can be removed here; the original code allowlist is view-only.
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs font-sans tracking-wider uppercase text-text-muted">
          <span className="inline-flex items-center gap-1.5">
            <Users size={12} /> {rows.total_size || 0} total
          </span>
        </div>
      </div>

      {/* ADD FORM */}
      <form onSubmit={onAdd} className="rounded-2xl border border-dark/10 bg-bone/40 p-6 space-y-4" data-testid="beta-tester-add-form">
        <div className="flex items-center gap-2 mb-2">
          <UserPlus size={16} className="text-gold" />
          <span className="font-sans text-xs tracking-[0.25em] uppercase text-gold">Add New Tester</span>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <Label htmlFor="new-tester-email" className="font-sans text-xs tracking-wider uppercase text-text-muted">Email</Label>
            <Input
              id="new-tester-email"
              type="email"
              placeholder="jane@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 bg-white border-dark/15 font-sans"
              data-testid="beta-tester-email-input"
              autoComplete="off"
              required
            />
          </div>
          <div>
            <Label htmlFor="new-tester-first-name" className="font-sans text-xs tracking-wider uppercase text-text-muted">First Name (or preferred greeting)</Label>
            <Input
              id="new-tester-first-name"
              placeholder="Jane"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="mt-1 bg-white border-dark/15 font-sans"
              data-testid="beta-tester-first-name-input"
              required
            />
          </div>
        </div>
        <div>
          <Label htmlFor="new-tester-note" className="font-sans text-xs tracking-wider uppercase text-text-muted">Note (optional)</Label>
          <Input
            id="new-tester-note"
            placeholder="e.g. referral from Brenda"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="mt-1 bg-white border-dark/15 font-sans"
            data-testid="beta-tester-note-input"
          />
        </div>
        <label className="flex items-center gap-2 text-sm font-sans text-dark/80 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={sendInvite}
            onChange={(e) => setSendInvite(e.target.checked)}
            className="h-4 w-4 accent-gold"
            data-testid="beta-tester-send-invite-toggle"
          />
          Send the Sept 11 main invite immediately
        </label>
        <div className="flex items-center gap-3">
          <Button
            type="submit"
            disabled={submitting}
            className="bg-dark text-bone hover:bg-dark/90 font-sans text-xs tracking-[0.2em] uppercase h-10 px-6 rounded-lg"
            data-testid="beta-tester-submit-btn"
          >
            {submitting ? (<><Loader2 size={14} className="mr-2 animate-spin" /> Adding&hellip;</>) : 'Add Tester'}
          </Button>
          {!sendInvite && (
            <span className="font-sans text-xs text-text-muted">No invite will be sent — they'll only get scheduled reminders.</span>
          )}
        </div>
      </form>

      {/* DB EXTRAS TABLE (removable) */}
      <div>
        <div className="flex items-baseline justify-between mb-3">
          <h3 className="font-serif text-xl text-dark">Runtime additions</h3>
          <span className="font-sans text-xs text-text-muted">{rows.db_extras_size || 0} testers · added via this dashboard</span>
        </div>
        {loading && <p className="font-sans text-sm text-text-muted">Loading&hellip;</p>}
        {!loading && (rows.db_extras || []).length === 0 && (
          <p className="font-sans text-sm text-text-muted italic">No runtime additions yet. Use the form above to add your first.</p>
        )}
        {!loading && (rows.db_extras || []).length > 0 && (
          <div className="overflow-x-auto rounded-xl border border-dark/10" data-testid="beta-tester-runtime-table">
            <table className="w-full text-sm font-sans">
              <thead className="bg-dark/[0.03] text-text-muted uppercase text-[10px] tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">First Name</th>
                  <th className="text-left px-4 py-3">Email</th>
                  <th className="text-left px-4 py-3">Note</th>
                  <th className="text-left px-4 py-3">Added</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {rows.db_extras.map((r) => (
                  <tr key={r.email} className="border-t border-dark/5" data-testid={`beta-tester-row-${r.email}`}>
                    <td className="px-4 py-3 text-dark">{r.first_name || <span className="text-text-muted italic">—</span>}</td>
                    <td className="px-4 py-3 text-dark/80">{r.email}</td>
                    <td className="px-4 py-3 text-text-muted">{r.note || <span className="italic">—</span>}</td>
                    <td className="px-4 py-3 text-text-muted">{r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}</td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onRemove(r)}
                        className="text-red-600 hover:bg-red-50 h-8 px-2"
                        data-testid={`beta-tester-remove-${r.email}`}
                      >
                        <Trash2 size={13} className="mr-1" /> Remove
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* CODE ALLOWLIST (view-only) */}
      <div>
        <div className="flex items-baseline justify-between mb-3">
          <h3 className="font-serif text-xl text-dark">Original cohort</h3>
          <span className="font-sans text-xs text-text-muted inline-flex items-center gap-1.5">
            <ShieldCheck size={12} /> {rows.code_allowlist_size || 0} testers · view-only
          </span>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="beta-tester-code-list">
          {(rows.code_allowlist || []).map((e) => (
            <Badge key={e} className="bg-bone text-dark border border-dark/10 font-sans text-xs px-3 py-1">
              {e}
            </Badge>
          ))}
        </div>
        <p className="font-sans text-xs text-text-muted italic mt-3">
          To remove someone from the original cohort, a code change is needed.
        </p>
      </div>
    </div>
  );
}
